/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/


import React, {useContext, useEffect, useRef, useState} from "react";

import {useDispatch} from "react-redux";
import {useTypedSelector} from "@Services/Hooks/UseTypedSelector";
import CHANGE_API_REQUEST_STATUS_ACTION from "@Actions/Interface/ChangeApiRequestStatus";

import APPLICATION_ROUTER_INTERFACE from "@ROUTER/Interfaces/Router";
import ApplicationRouterContext from "@Contexts/Router";

import ApplicationApiContext from "@Contexts/Api";


import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";
import {ERROR_SYSTEM_MESSAGE_INTERFACE} from "@Services/Application/Interfaces/_Messages/Errors";
import {HTTP_ERROR_RESPONSE_INTERFACE} from "@Services/Interfaces/Http/Response/Error";

import {TERMINAL_INTERFACE} from "@Services/Application/Interfaces/Terminals/Terminal";


import {Redirect} from "react-router-dom";


import ErrorMessageComponent from "@Components/UI/Communicates/Messages/Error";

import BlockContentHeaderComponent from "@Components/UI/Containers/Block/Header";
import BlockContentContainerComponent from "@Components/UI/Containers/Block";


import FavouriteTerminalsListComponent from "@UIPages/Authenticated/Settings/Favorite/Terminals/Favourites";
import AddFavouriteTerminalComponent from "@UIPages/Authenticated/Settings/Favorite/Terminals/AddToFavourite";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";
import REST_API_REQUEST from "@Services/Utilities/Requests";


const DEFAULT_REQUEST_HEADERS: HeadersInit = {"content-Type": "application/json"};


type MyFavouriteTerminalsPageProps = {};


export const SettingsMyFavouriteTerminalsPageComponent: React.FC<MyFavouriteTerminalsPageProps> = (): JSX.Element | null => {


    /** @const Global State Actions Dispatcher */
    const DISPATCHER = useDispatch();


    /** @const {React.Ref} My Favourite Terminals Page Container DOM Ref */
    const COMPONENT_REF: React.Ref<HTMLDivElement | null> = useRef(null);


    /** @const {UserInterface | null} Application Logged User (from Global Store Object) */
    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state): ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    /** @const {string} Global Rest Api Root URL Address */
    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL || null;

    /** @const {object} REST_API_DATA - Application State Store Rest Api Data */
    const REST_API_DATA:ReturnType<any> = useTypedSelector(
        (state) => state.api
    );

    /** @const {} REST_API_REQUEST_TOKEN - Application Rest Api Request Token */
    const REST_API_REQUEST_TOKEN: AUTHORIZATION_TOKEN_INTERFACE = REST_API_DATA?.requestToken || null;


    /** @const {APPLICATION_ROUTER_INTERFACE} Global Router Object */
    const ROUTER: APPLICATION_ROUTER_INTERFACE = useContext(ApplicationRouterContext);


    /** @var {[object, function]} State - Terminals List Object, Terminals List Object Setter */
    const [TERMINALS_LIST, SET_TERMINALS_LIST] = useState<TERMINAL_INTERFACE[] | null>(null);
    /** @var {[boolean, function]} State - Terminals List Component Error, Terminals List Component Error Setter */
    const [TERMINALS_LIST_ERROR, SET_TERMINALS_LIST_ERROR] = useState<ERROR_SYSTEM_MESSAGE_INTERFACE | null>(null);

    useEffect(() => {

        if (TERMINALS_LIST) return;

        const TERMINALS_LIST_API_URL: string = REST_API_URL + "/Terminal/List";

        const TERMINALS_LIST_REQUEST_DATA = {
            UserId: LOGGED_USER?.userID,
            Mode: "S"
        };

        const GET_TERMINALS_LIST = async (): Promise<void | Function> => {

            try {

                const TERMINALS_LIST_RESPONSE = await REST_API_REQUEST(
                    TERMINALS_LIST_API_URL,
                    TERMINALS_LIST_REQUEST_DATA,
                    REST_API_REQUEST_TOKEN
                );

                if (TERMINALS_LIST_RESPONSE.status === 200) {
                    const CONTENT = await TERMINALS_LIST_RESPONSE.text();
                    if (CONTENT.trim()) {
                        SET_TERMINALS_LIST(JSON.parse(CONTENT)?.Terminals || []);
                    } else {
                        SET_TERMINALS_LIST([]);
                    }
                } else {
                    if (TERMINALS_LIST_RESPONSE.status === 409) {
                        const CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await TERMINALS_LIST_RESPONSE.json();
                        SET_TERMINALS_LIST_ERROR({ErrorCode: CONTENT.ErrorCode, UserMessage: CONTENT.UserMessage});
                    } else {
                        SET_TERMINALS_LIST_ERROR({ErrorCode: TERMINALS_LIST_RESPONSE.status, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
                    }
                }

            } catch (e) {
                SET_TERMINALS_LIST_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
            }

        };

        GET_TERMINALS_LIST();

        return () => {
            SET_TERMINALS_LIST(null);
            SET_TERMINALS_LIST_ERROR(null);
        }

    }, []);


    /** Check if User is Logged */
    if (!LOGGED_USER || !LOGGED_USER?.userID) return <Redirect to={"/logowanie"}/>; // User is not logged => Redirect to LOGIN Page


    /** Check if Router is present */
    if (!ROUTER) return <Redirect to={"/404"}/>; // No Router Object - Redirect to 404 Page


    const UPDATE_TERMINAL_LIST_HANDLER = (eventType: "error" | "remove" | "add", terminalId?: string | null): void => {

        if (eventType === "error") {
            SET_TERMINALS_LIST_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
            return;
        }

        let UPDATED_TERMINALS_LIST: TERMINAL_INTERFACE[] = [];

        if (["remove", "add"].includes(eventType)) {
            UPDATED_TERMINALS_LIST = TERMINALS_LIST ? TERMINALS_LIST.map(
                (terminal: TERMINAL_INTERFACE) =>
                    terminal.TerminalNo === terminalId ? {...terminal, Favorite: eventType === "add" ? true : false} : terminal
            ) : [];
        } else {
            SET_TERMINALS_LIST_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
        }

        SET_TERMINALS_LIST(UPDATED_TERMINALS_LIST);

    };


    const FAVORITE_TERMINALS_LIST = TERMINALS_LIST ? TERMINALS_LIST?.filter((terminal: TERMINAL_INTERFACE) => !!terminal.Favorite) : null;
    const NOT_FAVORITE_TERMINALS_LIST =TERMINALS_LIST ? TERMINALS_LIST?.filter((terminal: TERMINAL_INTERFACE) => !terminal.Favorite) : null;


    return (
        <BlockContentContainerComponent
            forwardRef={COMPONENT_REF}
            cssClasses={"settings-page-favorites-data-component favorite-terminals-page-component settings-page-view-component"}
        >

            <BlockContentHeaderComponent headerText={"Ulubione bazy paliw"} />

            {!!TERMINALS_LIST_ERROR && <ErrorMessageComponent messageText={TERMINALS_LIST_ERROR?.UserMessage} />}

            {!!!TERMINALS_LIST_ERROR && TERMINALS_LIST &&
	        <>
            <AddFavouriteTerminalComponent
                TERMINALS_LIST={NOT_FAVORITE_TERMINALS_LIST}
                TERMINALS_LIST_HANDLER={UPDATE_TERMINAL_LIST_HANDLER}
            />

            <FavouriteTerminalsListComponent
                TERMINALS_LIST={FAVORITE_TERMINALS_LIST}
                TERMINALS_LIST_HANDLER={UPDATE_TERMINAL_LIST_HANDLER}
            />
	        </>
            }

        </BlockContentContainerComponent>
    );

};


export default SettingsMyFavouriteTerminalsPageComponent;
